// ── compactLadder — orderbook data for a Compact outcome band (#105) ──
//
// The detailed table's ladder is built by `renderLadderRows`, a closure
// inside GameRows welded to that table's 12-column grid, its consensus-dot
// placement and its selection-box rails. None of that is exported, and none
// of it means anything to a band that isn't a table row. So the compact
// bands build their own book — but from the SAME shared primitives
// (LADDER_LEVELS / bidIsReal / mirrorCents / queueDepth / orderTooltip), so
// the two presentations cannot disagree about prices, depth or queue.
//
// Mirroring is the trap here. Hydra publishes BOTH venues in YES space, so
// the NO ladder is the YES book mirrored. Always mirrorCents() — a
// hand-rolled `100 - price` drifts on fractional cents, and a drifted level
// price makes a resting sub-cent order miss its exact-match price level.

import { mirrorCents } from '@/lib/pricing/subCent';
import {
  LADDER_LEVELS,
  bidIsReal,
  orderTooltip,
  queueDepth,
  type BidRowOrder,
} from '../ValueDashboard/ladder/helpers';
import type { BookLevel, MarketSide, VenueBook } from '../ValueDashboard/types';
import type { UserOrderSlice } from '../../hooks/useUserKalshiPOS';

/** One venue's visible book, expressed in ACTIVE-side (Yes/No) space. */
export interface CompactLadderSide {
  /** Padded to LADDER_LEVELS. Worst ask first, best ask last — so they read
   *  top-down ending at the last-trade strip, same as the detailed ladder. */
  asks: (BookLevel | null)[];
  /** Padded to LADDER_LEVELS. Best bid first, reading down from the strip. */
  bids: (BookLevel | null)[];
  /** Last trade, mirrored onto the active side. Null when the venue has
   *  never printed — the strip renders an em-dash rather than inventing a
   *  price (empty/unavailable states stay truthful). */
  lastCents: number | null;
}

function pad(rows: BookLevel[]): (BookLevel | null)[] {
  const out: (BookLevel | null)[] = [...rows];
  while (out.length < LADDER_LEVELS) out.push(null);
  return out;
}

/**
 * Build one venue's compact ladder. Mirrors the detailed ladder's derivation
 * exactly: the ASKS shown on the active side are the OPPOSITE side's resting
 * bids mirrored into active-side space (that is what you'd cross to buy),
 * and the BIDS are the active side's own resting bids.
 *
 * A missing book yields empty (all-null) rungs rather than a thrown error —
 * one-sided markets are normal here (Poly lists no book for most props).
 */
export function buildCompactLadderSide(
  book: VenueBook | null | undefined,
  marketSide: MarketSide,
): CompactLadderSide {
  if (!book) return { asks: pad([]), bids: pad([]), lastCents: null };

  const ownBids = marketSide === 'yes' ? book.yes : book.no;
  const oppositeBids = marketSide === 'yes' ? book.no : book.yes;

  // hydra reports the last trade as the YES last; invert for the NO side.
  const lastYes = book.lastPriceCents ?? null;
  const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : mirrorCents(lastYes);
  const oppositeLast =
    lastYes == null ? null : marketSide === 'yes' ? mirrorCents(lastYes) : lastYes;

  const asks = oppositeBids
    .filter(bidIsReal(oppositeLast))
    .slice(0, LADDER_LEVELS)
    // Exact mirror — the per-level order chips exact-match against these
    // prices, and a float `100 − p` misses a resting sub-cent order.
    .map((l) => ({ priceCents: mirrorCents(l.priceCents), size: l.size }));
  const bids = ownBids.filter(bidIsReal(ownLast)).slice(0, LADDER_LEVELS);

  return {
    // Reverse THEN pad, matching the detailed ladder: with fewer than
    // LADDER_LEVELS asks the blank rungs sit nearest the strip, so the best
    // ask keeps its position adjacent to the last-trade line.
    asks: pad([...asks].reverse()),
    bids: pad(bids),
    lastCents: ownLast,
  };
}

/**
 * Your resting Kalshi orders bucketed by price level, for the active side.
 *
 * Split by action because BUY orders rest on the bid stack and SELL orders
 * on the ask stack — the same split the detailed ladder makes. Largest
 * order first within a level — a stable INPUT order, not the display one:
 * #676 ranks by queue position where the list is truncated (`LadderLine`,
 * `RestingOrderGroup`), so every truncating surface cuts the same way. Poly
 * orders aren't tracked, so this is Kalshi-only, matching the detailed
 * ladder's behaviour.
 */
export function buildCompactOrdersByCents(
  book: VenueBook | null | undefined,
  ordersByTicker: Map<string, UserOrderSlice[]>,
  marketSide: MarketSide,
  action: 'buy' | 'sell',
): Map<number, BidRowOrder[]> {
  const map = new Map<number, BidRowOrder[]>();
  if (!book) return map;

  for (const o of ordersByTicker.get(book.marketTicker) ?? []) {
    if (o.action !== action) continue;
    if (o.side !== marketSide) continue;
    const list = map.get(o.priceCents) ?? [];
    list.push({
      orderId: o.orderId,
      remainingCount: o.remainingCount,
      queueOrBetter: queueDepth(o, book).atLevelOrBetter,
      tooltip: orderTooltip(o, book),
      // #676 — the band ranks confirmed queue readings ahead of remembered
      // ones before it truncates, so the provenance travels with the number.
      queueRetained: o.queueRetained,
    });
    map.set(o.priceCents, list);
  }
  for (const list of map.values()) list.sort((a, b) => b.remainingCount - a.remainingCount);
  return map;
}
